import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import {
  Heart, Brain, Users, GraduationCap, Loader2, Plus, Send,
  TrendingUp, AlertTriangle, CheckCircle2, MinusCircle
} from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";

const PARENT_CATEGORIES = [
  { value: "sleep", label: "النوم 😴" },
  { value: "appetite", label: "الشهية 🍽️" },
  { value: "mood", label: "المزاج 😊" },
  { value: "activity", label: "النشاط 🏃" },
  { value: "other", label: "أخرى 📝" },
];

const STATUS_CONFIG = {
  good: { label: "جيد", icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 dark:bg-emerald-950/30" },
  moderate: { label: "متوسط", icon: MinusCircle, color: "text-amber-500", bg: "bg-amber-100 dark:bg-amber-950/30" },
  needs_attention: { label: "يحتاج متابعة", icon: AlertTriangle, color: "text-red-500", bg: "bg-red-100 dark:bg-red-950/30" },
};

const chartConfig = {
  score: { label: "الدرجة", color: "hsl(var(--primary))" },
};

interface ParentChildReportProps {
  childId: string;
  childName: string;
  nurseryId: string;
}

const ParentChildReport = ({ childId, childName, nurseryId }: ParentChildReportProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [noteCategory, setNoteCategory] = useState("mood");
  const [noteContent, setNoteContent] = useState("");

  // Fetch latest weekly report
  const { data: weeklyReport, isLoading: reportLoading } = useQuery<any>({
    queryKey: ["weekly-report", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("child_weekly_reports" as any)
        .select("*")
        .eq("child_id", childId)
        .order("week_start", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!childId,
  });

  // Fetch recent observations
  const { data: observations } = useQuery({
    queryKey: ["parent-observations", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("child_observations" as any)
        .select("*")
        .eq("child_id", childId)
        .order("observation_date", { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!childId,
  });

  // Fetch parent's own notes
  const { data: parentNotes } = useQuery({
    queryKey: ["parent-notes", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("parent_notes" as any)
        .select("*")
        .eq("child_id", childId)
        .order("created_at", { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!childId,
  });

  // Add parent note
  const addNote = useMutation({
    mutationFn: async () => {
      if (!user || !noteContent.trim()) throw new Error("بيانات ناقصة");
      const { error } = await supabase.from("parent_notes" as any).insert({
        nursery_id: nurseryId,
        child_id: childId,
        parent_id: user.id,
        category: noteCategory,
        content: noteContent.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم إضافة الملاحظة");
      setNoteContent("");
      queryClient.invalidateQueries({ queryKey: ["parent-notes", childId] });
    },
    onError: () => toast.error("فشل إضافة الملاحظة"),
  });

  const radarData = weeklyReport ? [
    { dimension: "تعليمي", score: weeklyReport.academic_score * 20 },
    { dimension: "صحي", score: weeklyReport.health_score * 20 },
    { dimension: "نفسي", score: weeklyReport.psychological_score * 20 },
    { dimension: "سلوكي", score: weeklyReport.behavioral_score * 20 },
  ] : [];

  const statusInfo = weeklyReport ? STATUS_CONFIG[weeklyReport.overall_status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.moderate : null;

  const getScoreColor = (score: number) => {
    if (score >= 4) return "text-emerald-500";
    if (score >= 3) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-foreground">التقرير الشامل - {childName}</h3>

      {reportLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : !weeklyReport ? (
        <Card className="shadow-card">
          <CardContent className="py-12 text-center">
            <Brain className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">لا يوجد تقرير أسبوعي بعد لهذا الطفل</p>
            <p className="text-xs text-muted-foreground mt-1">سيتم إنشاء التقارير تلقائياً كل أسبوع</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Overall Status */}
          {statusInfo && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className={`shadow-card border-r-4 ${
                weeklyReport.overall_status === 'good' ? 'border-r-emerald-500' :
                weeklyReport.overall_status === 'moderate' ? 'border-r-amber-500' : 'border-r-red-500'
              }`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${statusInfo.bg}`}>
                    <statusInfo.icon className={`w-6 h-6 ${statusInfo.color}`} />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">الحالة العامة: {statusInfo.label}</h4>
                    <p className="text-xs text-muted-foreground">
                      أسبوع {format(new Date(weeklyReport.week_start), "d MMM", { locale: ar })} - {format(new Date(weeklyReport.week_end), "d MMM yyyy", { locale: ar })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Dimension Scores + Radar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Score Indicators */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="shadow-card h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">المؤشرات الأربعة</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: "التعليمي", score: weeklyReport.academic_score, icon: GraduationCap },
                    { label: "الصحي", score: weeklyReport.health_score, icon: Heart },
                    { label: "النفسي", score: weeklyReport.psychological_score, icon: Brain },
                    { label: "السلوكي", score: weeklyReport.behavioral_score, icon: Users },
                  ].map((dim, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <dim.icon className={`w-5 h-5 ${getScoreColor(dim.score)} shrink-0`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{dim.label}</span>
                          <span className={`text-sm font-bold ${getScoreColor(dim.score)}`}>{dim.score}/5</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full ${
                              dim.score >= 4 ? "bg-emerald-500" : dim.score >= 3 ? "bg-amber-500" : "bg-red-500"
                            }`}
                            initial={{ width: 0 }}
                            animate={{ width: `${(dim.score / 5) * 100}%` }}
                            transition={{ delay: 0.2 + i * 0.1, duration: 0.6 }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Radar Chart */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card className="shadow-card h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">الرسم البياني</CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={chartConfig} className="h-[250px] w-full">
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="dimension" className="text-xs" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                      <Radar
                        name="الدرجة"
                        dataKey="score"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                    </RadarChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* AI Suggestions */}
          {weeklyReport.ai_suggestions && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="shadow-card border-r-4 border-r-primary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    اقتراحات ذكية
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {weeklyReport.ai_suggestions}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </>
      )}

      {/* Teacher Observations */}
      {observations && observations.length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">آخر ملاحظات المعلم</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-3">
                {observations.map((obs: any) => (
                  <div key={obs.id} className="p-3 rounded-lg bg-muted/50 border">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        {obs.category === "health" ? "صحي" : obs.category === "psychological" ? "نفسي" : "سلوكي"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(obs.observation_date), "d MMM", { locale: ar })}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {(obs.tags || []).map((tag: string) => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                    {obs.notes && <p className="text-xs text-muted-foreground mt-1">{obs.notes}</p>}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Parent Notes */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            ملاحظاتك من البيت
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Select value={noteCategory} onValueChange={setNoteCategory}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PARENT_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="مثال: نام متأخراً، شهيته ضعيفة، مزاجه جيد..."
              value={noteContent}
              onChange={e => setNoteContent(e.target.value)}
              rows={2}
              className="resize-none flex-1"
            />
          </div>
          <Button
            size="sm"
            onClick={() => addNote.mutate()}
            disabled={!noteContent.trim() || addNote.isPending}
            className="gap-1.5"
          >
            {addNote.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            إضافة ملاحظة
          </Button>

          {parentNotes && parentNotes.length > 0 && (
            <ScrollArea className="max-h-[200px] mt-4">
              <div className="space-y-2">
                {parentNotes.map((note: any) => (
                  <div key={note.id} className="p-2.5 rounded-lg bg-primary/5 border border-primary/10 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-xs">
                        {PARENT_CATEGORIES.find(c => c.value === note.category)?.label || note.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(note.created_at), "d MMM", { locale: ar })}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-xs">{note.content}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentChildReport;
